/**
 * Timezone-safe date/time utility functions.
 *
 * This is the single place that knows about moment-timezone.
 * Both ReservationForm and FilterForm import from here instead
 * of implementing their own timezone handling.
 *
 * Fixes the FilterForm timezone bug where plain moment() was used
 * instead of server-timezone-aware operations.
 */
import moment from 'moment-timezone';
import { DATEPICKER_LOCALES } from '../constants/booking';

/**
 * Create a server-time moment from a server time string and timezone.
 * Replaces ad-hoc `moment.tz(serverTime, timezone)` calls.
 *
 * @param {string} serverTime - Server time string
 * @param {string} timezone - IANA timezone identifier (e.g. 'Atlantic/Canary')
 * @returns {moment.Moment}
 */
export function createServerMoment(serverTime, timezone) {
    return moment.tz(serverTime, timezone);
}

/**
 * Convert a JS Date to a moment in the server timezone.
 * Preserves the calendar date (year/month/day) regardless of browser TZ.
 *
 * Replaces ReservationForm.toServerMoment and fixes FilterForm's
 * use of plain moment().
 *
 * @param {Date} date - JS Date object
 * @param {string} timezone - IANA timezone identifier
 * @returns {moment.Moment}
 */
export function toServerMoment(date, timezone) {
    return moment.tz(
        [date.getFullYear(), date.getMonth(), date.getDate()],
        timezone
    );
}

/**
 * Format a Date for server submission without TZ shift.
 * Constructs a moment in the server timezone from the Date's local
 * year/month/day, then formats it.
 *
 * Replaces ReservationForm.formatDateForServer.
 *
 * @param {Date} date - JS Date object
 * @param {string} timezone - IANA timezone identifier
 * @param {string} [format='DD/MM/YYYY'] - Output format
 * @returns {string}
 */
export function formatDateForServer(date, timezone, format) {
    if (!format) { format = 'DD/MM/YYYY'; }
    var year = date.getFullYear();
    var month = date.getMonth() + 1;
    var day = date.getDate();
    return moment.tz(year + '-' + month + '-' + day, 'YYYY-M-D', timezone).format(format);
}

/**
 * Get the formatted moment for server submission (for cases that need the moment object).
 *
 * @param {Date} date - JS Date object
 * @param {string} timezone - IANA timezone identifier
 * @returns {moment.Moment}
 */
export function formatDateMomentForServer(date, timezone) {
    var year = date.getFullYear();
    var month = date.getMonth() + 1;
    var day = date.getDate();
    return moment.tz(year + '-' + month + '-' + day, 'YYYY-M-D', timezone);
}

/**
 * Strip time from a Date, returning midnight of that date.
 * Replaces the duplicated getDateOf(date) in RF and FF.
 *
 * @param {Date} date
 * @returns {Date}
 */
export function getDateOf(date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Check if a date is today in the server timezone.
 *
 * @param {Date} date
 * @param {moment.Moment} serverTimeMoment
 * @param {string} timezone
 * @returns {boolean}
 */
export function isToday(date, serverTimeMoment, timezone) {
    var momentDate = toServerMoment(date, timezone);
    return serverTimeMoment.isSame(momentDate, 'day');
}

/**
 * Check if two dates are the same calendar day.
 *
 * @param {Date} date1
 * @param {Date} date2
 * @returns {boolean}
 */
export function isSameDay(date1, date2) {
    return getDateOf(date1).getTime() === getDateOf(date2).getTime();
}

/**
 * Clamp an hour value within bounds.
 *
 * @param {number|string} hour
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export function clampHour(hour, min, max) {
    return Math.max(min, Math.min(max, parseInt(hour)));
}

/**
 * Get the datepicker locale object for the current app locale.
 *
 * @param {string} locale - 'en', 'es', or 'de'
 * @returns {Object}
 */
export function getDatepickerLocale(locale) {
    return DATEPICKER_LOCALES[locale];
}
